/**
 * D418: the one buy, unlock, hire or build button. Every one has the same width and height
 * (`--buy-w`, `--buy-h` in idle.css); a long price wraps inside it rather than widening it.
 * The shortfall sits under it, as `Needs 300c`.
 */
import type { ComponentChildren } from 'preact';
import { Button } from '../ui/Button.tsx';
import { UNSTRUCK } from '../ui/OffNote.tsx';
import { currencyShort } from './terms.ts';

/** A figure inside a line of words: `300c`, `1.6c/s`, `10.2Kc`, `25`, `2.4e15c`. */
const figure = (): RegExp => {
  const unit = currencyShort().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(\\d[\\d,.]*(?:e\\d+)?[KMBT]?(?:${unit})?(?:/s)?)`);
};

/**
 * A line of words with each figure set in tabular figures (D458). The line is a mixed one, so
 * the figure keeps the UI font: mono is for a number standing alone, and a button's price line
 * (which sits inside a `.num` parent, so the figure inherits mono there). Replaces D261's mono
 * figure inside a line of words.
 */
export function Figs({ text }: { text: string }) {
  const parts = text.split(figure());
  return (
    <>
      {parts.map((p, i) =>
        i % 2 ? (
          <span key={i} class="tnum">
            {p}
          </span>
        ) : (
          p
        ),
      )}
    </>
  );
}

/**
 * D439: the one button shape. 44px tall (48px full width), 8px radius, two lines: the action
 * (13px 600) over its count or price (11px 500 mono). Four weights: primary (accent fill),
 * secondary (surface, a ring), secondary accent (an accent ring and accent text) and disabled
 * (base.css: the off box, label struck through, the shortfall line not struck).
 */
export const BUY_STYLE = {
  width: 'var(--buy-w)',
  height: 'var(--buy-h)',
  padding: '0 6px',
  borderRadius: 'var(--radius-md)',
  font: '600 var(--buy-font)/1.15 var(--font-ui)',
  whiteSpace: 'normal',
  textAlign: 'center',
  overflowWrap: 'anywhere',
} as const;

/** The two lines inside a button: the action, then its price in the number font. */
export function BtnLabel({
  action,
  price,
}: {
  action: ComponentChildren;
  price: ComponentChildren;
}) {
  return (
    <span style={{ display: 'grid', gap: 3, justifyItems: 'center', minWidth: 0 }}>
      <span style={{ font: '600 13px/1 var(--font-ui)', whiteSpace: 'nowrap' }}>{action}</span>
      <span class="num" style={{ font: '500 11px/1 var(--font-num)', whiteSpace: 'nowrap' }}>
        {/* A grid item is blockified, so only an inner inline block escapes the strike. */}
        <span style={UNSTRUCK}>{price}</span>
      </span>
    </span>
  );
}

export function BuyButton({
  children,
  needs,
  locked,
  onClick,
  label,
  testId,
  fill = false,
  outline = false,
  tone,
  width,
  secondary = false,
}: {
  children: ComponentChildren;
  /** Why it cannot be pressed now: `Needs 300c`, or a lock line. */
  needs?: string | undefined;
  locked?: boolean;
  onClick: (e: MouseEvent) => void;
  label?: string;
  testId?: string;
  /** Fill the card's width, for a card narrower than the button's usual width. */
  fill?: boolean;
  /** D431: the quiet form, for a Hire: surface fill, an accent ring and accent text. */
  outline?: boolean;
  /** D431: a perk bought with Heritage takes the money colour. */
  tone?: 'coin';
  /** D442: a fixed width in px where the card's is not the usual (96 for an upgrade or perk). */
  width?: number;
  /** D442: a plain secondary button (surface, a ring) when it can be pressed. */
  secondary?: boolean;
}) {
  const off = needs !== undefined || !!locked;
  return (
    <span
      data-testid={testId}
      style={{
        display: 'grid',
        justifyItems: fill ? 'stretch' : 'end',
        gap: 2,
        width: fill ? '100%' : (width ?? 'var(--buy-w)'),
        flex: 'none',
      }}
    >
      <Button
        variant={off || secondary ? 'secondary' : 'primary'}
        disabled={off}
        explain={needs}
        onClick={onClick}
        style={{
          ...(fill
            ? { ...BUY_STYLE, width: '100%', height: 'var(--buy-h-full)' }
            : { ...BUY_STYLE, ...(width ? { width } : {}) }),
          ...(tone === 'coin' && !off
            ? {
                background: 'var(--coin)',
                color: 'var(--on-coin)',
                borderColor: 'var(--coin-edge)',
              }
            : {}),
          ...(outline && !off
            ? {
                background: 'var(--surface)',
                color: 'var(--accent)',
                borderColor: 'transparent',
                boxShadow: 'inset 0 0 0 1.5px var(--accent)',
              }
            : {}),
        }}
        {...(label ? { 'aria-label': label } : {})}
      >
        {children}
      </Button>
      {needs !== undefined && (
        <span
          style={{
            font: '400 var(--card-note) / 1.2 var(--font-ui)',
            color: 'var(--ink)',
            textAlign: 'right',
            ...(width ? { whiteSpace: 'nowrap' as const } : {}),
          }}
        >
          <Figs text={needs} />
        </span>
      )}
    </span>
  );
}
